// Whether two rate observations can be set against each other. Pure and
// read-only: it never converts currency, unit, period or burden (any conversion
// is a consumer's explicit assumption, never a silent step here) and never
// stands in for an unknown. A delta exists only when every measured dimension
// matches; otherwise the caller shows the reasons.
const TYPE_WORDS = {
    CEILING: 'ceiling',
    PROPOSED: 'proposed',
    SUBK_AGREED: 'subk agreed',
    BILLED: 'billed',
    INFERRED: 'inferred',
    AWARDED: 'awarded'
};
const BURDEN_WORDS = {
    FULLY_BURDENED: 'fully burdened',
    BURDENED_NO_FEE: 'burdened, no fee',
    UNBURDENED: 'unburdened'
};
const norm = (s)=>(s ?? '').trim().toLowerCase();
const capturedAmount = (n)=>Number.isFinite(n) && n > 0;
// One reason for a free-text dimension that is either stated on both sides and
// equal (no reason), stated on one side only, or stated differently.
const textReason = (name, a, b)=>{
    const x = (a ?? '').trim();
    const y = (b ?? '').trim();
    if (norm(x) === norm(y)) return null;
    if (!x || !y) return `${name} stated on one side only`;
    return `different ${name} (${x} vs ${y})`;
};
const periodReason = (a, b)=>{
    const ka = a.normalizedPeriodKey?.trim();
    const kb = b.normalizedPeriodKey?.trim();
    const la = a.periodLabel?.trim();
    const lb = b.periodLabel?.trim();
    if (ka && kb) {
        return norm(ka) === norm(kb) ? null : `different period (${la || ka} vs ${lb || kb})`;
    }
    if (!la && !lb) return 'period not stated';
    if (!la || !lb) return 'period not stated on one side';
    return norm(la) === norm(lb) ? null : `different period (${la} vs ${lb})`;
};
const burdenReason = (a, b)=>{
    const x = BURDEN_WORDS[a];
    const y = BURDEN_WORDS[b];
    if (!x && !y) return 'burden basis not stated';
    if (!x || !y) return 'burden basis not stated on one side';
    return x === y ? null : `different burden basis (${x} vs ${y})`;
};
export function compareRates(base, other) {
    const reasons = [];
    if (base.entryId !== other.entryId) {
        reasons.push('different role definitions (a shared occupational anchor is grouping, not equivalence)');
    }
    if (base.observationType !== other.observationType) {
        const word = (t)=>TYPE_WORDS[t] ?? t.toLowerCase();
        reasons.push(`different evidence types (${word(base.observationType)} vs ${word(other.observationType)})`);
    }
    if (norm(base.currency) !== norm(other.currency)) {
        reasons.push(`different currency (${base.currency} vs ${other.currency})`);
    }
    if (norm(base.rateUnit) !== norm(other.rateUnit)) {
        reasons.push(`different unit (${base.rateUnit.toLowerCase()} vs ${other.rateUnit.toLowerCase()})`);
    }
    for (const r of [
        periodReason(base, other),
        burdenReason(base.burdenBasis, other.burdenBasis),
        textReason('worksite', base.worksite, other.worksite),
        textReason('locality', base.locality, other.locality)
    ]){
        if (r) reasons.push(r);
    }
    if (!capturedAmount(base.amount) || !capturedAmount(other.amount)) {
        reasons.push('amount not captured');
    }
    if (reasons.length > 0) return {
        comparable: false,
        reasons
    };
    const delta = other.amount - base.amount;
    return {
        comparable: true,
        delta,
        percent: delta / base.amount * 100
    };
}
